import { useEffect, useState } from 'react';
import { Flame } from 'lucide-react';

/**
 * Phase 2M PR 2 — Kickoff Countdown.
 *
 * Tick down to kickoff with kinetic hype copy.  Only renders when
 * the room reports `seconds_to_kickoff > 0` and within a reasonable window.
 *
 *   <ArenaxKickoffCountdown initialSeconds={room.seconds_to_kickoff}
 *                              spectatorCount={5}
 *                              matchLabel="HSR Titans vs Strikers FC" />
 */
export default function ArenaxKickoffCountdown({ initialSeconds, spectatorCount = 0, matchLabel }) {
  const [secs, setSecs] = useState(initialSeconds || 0);

  useEffect(() => {
    if (!initialSeconds || initialSeconds <= 0) return;
    setSecs(initialSeconds);
    const tid = setInterval(() => {
      setSecs(s => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(tid);
  }, [initialSeconds]);

  if (!secs || secs <= 0) return null;
  // Render bigger / brighter as we get closer
  const urgent = secs <= 60;
  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');

  return (
    <div className={`rounded-2xl border ${urgent ? 'border-amber-500/55' : 'border-amber-500/30'} p-5 text-center`}
      data-testid="arenax-kickoff-countdown"
      style={{
        background: urgent
          ? 'linear-gradient(135deg, rgba(245,158,11,0.18), var(--ax-card-bg-soft))'
          : 'linear-gradient(135deg, rgba(245,158,11,0.08), var(--ax-card-bg-soft))',
      }}>
      <div className="flex items-center justify-center gap-2 mb-2">
        <Flame className={`h-4 w-4 text-amber-300 ${urgent ? 'animate-pulse' : ''}`} />
        <span className="text-[10px] tracking-[0.3em] text-amber-300 font-black uppercase">
          Kickoff in
        </span>
      </div>
      <div className={`tabular-nums font-black text-amber-200 ${urgent ? 'text-5xl' : 'text-3xl'} leading-none`}
        data-testid="arenax-kickoff-countdown-time">
        {mm}:{ss}
      </div>
      {matchLabel && (
        <p className="text-xs text-slate-300 mt-2">
          {matchLabel}
        </p>
      )}
      {spectatorCount >= 3 && (
        <p className="text-[10px] uppercase tracking-wider text-cyan-200 font-bold mt-2">
          🔥 {spectatorCount} waiting live
        </p>
      )}
    </div>
  );
}
